import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ConnectedPagesTree } from '../ConnectedPagesTree/ConnectedPagesTree';
import { ConnectedPagesGraph } from '../ConnectedPagesGraph/ConnectedPagesGraph';
import { OrgChartTree } from '../OrgChartTree/OrgChartTree';
import Btn, { BtnModifier } from '../../../../components/molecules/Btn';
import { PageType } from '../../../../types/api/PageType';
import { ISonURL } from '../../../../types/IUrl';
import { ContentLink } from '../../ContentLink';
import { Icons, SVG } from '../../../../components/atoms/SVG';
import { IConnectedPage } from '../../../../types/api/connectedPages/IConnectedPage';
import { ConnectedPagesMode } from '../../../../types/api/connectedPages/ConnectedPagesMode';
import { ConnectedPagesPageTypes } from '../../../../types/api/connectedPages/ConnectedPagesPageTypes';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { Breakpoints } from '../../../../types/Breakpoints';
import { useGlobalPrintMode } from '../../../../contexts/GlobalPrintMode';
import { GlobalDocumentIds } from '../../../../types/GlobalDocumentIds';
import { useAppContext } from '../../../../contexts/AppContext/AppContext';

export type ConnectedPagesProperties = {
  connectedPages: IConnectedPage[];
  pageType: ConnectedPagesPageTypes;
  editModeActive: boolean;
  initialVisibleDepth: number;
  modes: ConnectedPagesMode[];
  initialMode: ConnectedPagesMode;
  linkToOrgUnitHome?: ISonURL;
  secondLevelDedicatedOrgUnitVisualizationIndices?: number[];
};

/**
 * Component to switch between 3 different modes, all set up on the same data.
 *
 * Switching is done by manipulating the URL, and listening to the changes of the url.
 * available modes:
 * -  ?mode=tree -> the List/Tree view
 * -  ?mode=graph -> Bubblegraph
 * -  ?mode=org -> Organigramm
 *  */
export function ConnectedPages({
  connectedPages,
  pageType,
  editModeActive,
  initialVisibleDepth,
  modes,
  initialMode,
  linkToOrgUnitHome,
}: ConnectedPagesProperties) {
  const { t } = useSonTranslation('ConnectedPages');
  const [searchParams, setSearchParams] = useSearchParams();
  const { isInGlobalPrintMode } = useGlobalPrintMode();
  const { windowInnerWidth } = useAppContext();

  // init mode priority 1. printMode, 2. url bar, 3. data in json
  const [currentMode, setCurrentMode] = useState<ConnectedPagesMode>(() => {
    if (isInGlobalPrintMode) {
      return 'tree';
    }

    return searchParams.get('mode') as ConnectedPagesMode || initialMode;
  });

  // change location mode if its not the same
  const setModeSearchParam = useCallback(
    (mode: ConnectedPagesMode) => {
      setSearchParams((prevSearchParams) => {
        prevSearchParams.set('mode', mode);

        return new URLSearchParams([...prevSearchParams]);
      }, { state: { refetch: 'ignoreQuery' } });
    },
    [setSearchParams],
  );

  useEffect(() => {
    const navigateToModeTree = () => setModeSearchParam('tree');
    // switching to tree mode for pinting, we dont want to print graph or org for now
    window.addEventListener('beforeprint', navigateToModeTree);
    // clean up eventlistener!
    return () => {
      window.removeEventListener('beforeprint', navigateToModeTree);
    };
  }, [setModeSearchParam]);

  useEffect(() => {
    if (
      windowInnerWidth > Breakpoints.base &&
      windowInnerWidth < Breakpoints.s_tablet_portrait &&
      currentMode !== 'tree'
    ) {
      setModeSearchParam('tree');
    }
  }, [windowInnerWidth, currentMode, setModeSearchParam]);

  // setting the mode from url
  useEffect(() => {
    const modeInUrl = searchParams.get('mode') as ConnectedPagesMode;
    if (modeInUrl) {
      setCurrentMode(modeInUrl);
    }
  }, [searchParams]);

  // Link back to home only from the second level at Organigramm view
  const getLinkToHome = () => {
    if (pageType === PageType.orgunit && linkToOrgUnitHome) {
      return (
        <div className="connected-pages__link-to-home">
          <ContentLink to={`${linkToOrgUnitHome.url}?mode=${currentMode}`}>
            <SVG svgSpriteId={Icons.PAGE_BACK} svgClass="back-to-parent__icon" />
            {t('backToOrgHome')}
          </ContentLink>
        </div>
      );
    }
    return null;
  };

  const renderConnectedPages = () => {
    switch (currentMode) {
      case 'org':
        return (
          <OrgChartTree
            connectedPages={connectedPages}
            pageType={pageType}
            initialVisibleDepth={initialVisibleDepth}
          />
        );
      case 'tree':
        return (
          <ConnectedPagesTree connectedPages={connectedPages[0].childPages} initialVisibleDepth={initialVisibleDepth} />
        );
      case 'graph':
        return <ConnectedPagesGraph headlineLevel={3} connectedPages={connectedPages} />;
      default:
        return null;
    }
  };

  const renderSwitchButton = (mode: ConnectedPagesMode) => {
    return (
      modes.includes(mode) && (
        <Btn
          modifier={[BtnModifier.GROW, BtnModifier.TOGGLE]}
          text={t(`viewOptions.${mode}`)}
          onClick={() => setModeSearchParam(mode)}
          ariaPressed={currentMode === mode}
          ariaControls={GlobalDocumentIds.connectedPagesViewContainer}
          ariaExpanded={currentMode === mode}
        />
      )
    );
  };

  const renderSwitchButtons = () => {
    if ((modes && modes.length < 2) || editModeActive) {
      return null;
    }

    return (
      <div className="connected-pages__view">
        <h3 className="invisible">{t('viewOptions.headline')}</h3>
        <div className="connected-pages__view-btn-container">
          {renderSwitchButton('tree')}
          {renderSwitchButton('graph')}
          {renderSwitchButton('org')}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="connected-pages">
        <h2 className="connected-pages__headline invisible">{t(`viewOptions.${currentMode}`)}</h2>
        {renderSwitchButtons()}
      </div>
      {/* container referenced by the buttons as expanded */}
      <div id={GlobalDocumentIds.connectedPagesViewContainer}>
        {getLinkToHome()}
        {renderConnectedPages()}
      </div>
    </>
  );
}
